import { Injectable } from '@angular/core';
import { Config } from '../../../infrastructure/config';

export type CurrencyCode = string;

type ExchangeRatesMap = Record<string, number>;
type SymbolsMap = Record<string, string>;

type DualStyle = 'paren' | 'stack';

@Injectable({ providedIn: 'root' })
export class CurrencyService {
  private readonly cfg = Config.currency;

  private get rates(): ExchangeRatesMap {
    return (this.cfg.exchangeRates as unknown as ExchangeRatesMap) || {};
  }

  private get symbols(): SymbolsMap {
    return (this.cfg.symbols as unknown as SymbolsMap) || {};
  }

  getBaseCurrency(): CurrencyCode {
    return this.cfg.base;
  }

  getSecondaryCurrency(): CurrencyCode {
    return this.cfg.secondary;
  }

  shouldShowDual(override?: boolean): boolean {
    return typeof override === 'boolean' ? override : !!this.cfg.dualDisplay;
  }

  getDualStyle(override?: DualStyle): DualStyle {
    const style = override ?? (this.cfg.dualStyle as DualStyle | undefined);
    return style === 'stack' ? 'stack' : 'paren';
  }

  toNumber(value: unknown): number {
    if (value === null || value === undefined) { return 0; }

    const normalized = String(value).trim().replace(',', '.');
    const num = Number.parseFloat(normalized);

    return Number.isFinite(num) ? num : 0;
  }

  private getRate(from: CurrencyCode, to: CurrencyCode): number | null {
    if (from === to) { return 1; }

    const key = `${from}_${to}`;
    const rate = this.rates[key];

    if (!Number.isFinite(rate)) {
      console.warn(`[CurrencyService] Missing/invalid exchange rate: ${key}`);
      return null;
    }

    return rate;
  }

  convert(amount: number, from: CurrencyCode, to: CurrencyCode): number {
    if (from === to) { return amount; }

    const rate = this.getRate(from, to);
    if (rate === null) { return amount; }

    return amount * rate;
  }

  symbol(currency: CurrencyCode): string {
    return this.symbols[currency] || currency;
  }

  format(amount: number, currency: CurrencyCode): string {
    const safe = Number.isFinite(amount) ? amount : 0;

    const fixed = safe.toFixed(this.cfg.fractionDigits);
    const [intRaw = '0', decRaw = '00'] = fixed.split('.');

    const grouped = intRaw.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      this.cfg.thousandsSeparator
    );

    const symbol = this.symbol(currency);

    return `${grouped}${this.cfg.decimalSeparator}${decRaw} ${symbol}`;
  }

  formatInBase(amount: unknown, sourceCurrency?: CurrencyCode): string {
    const num = this.toNumber(amount);

    const from = sourceCurrency || this.getBaseCurrency();
    const base = this.getBaseCurrency();

    const baseAmount = this.convert(num, from, base);
    return this.format(baseAmount, base);
  }

  formatSecondary(amount: unknown, sourceCurrency?: CurrencyCode, dualDisplayOverride?: boolean): string | null {
    if (!this.shouldShowDual(dualDisplayOverride)) { return null; }

    const num = this.toNumber(amount);
    const from = sourceCurrency || this.getBaseCurrency();

    const base = this.getBaseCurrency();
    const secondary = this.getSecondaryCurrency();

    const baseAmount = this.convert(num, from, base);
    const secondaryAmount = this.convert(baseAmount, base, secondary);

    const approx = this.cfg.showApproxSign ? '≈ ' : '';
    return `${approx}${this.format(secondaryAmount, secondary)}`;
  }

  formatParts(amount: number, currency: CurrencyCode): {
    integer: string;
    fraction: string;
    symbol: string;
  } {
    const fixed = amount.toFixed(this.cfg.fractionDigits);
    const [intRaw = '0', decRaw = ''] = fixed.split('.');

    const grouped = intRaw.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      this.cfg.thousandsSeparator
    );

    return {
      integer: grouped,
      fraction: this.cfg.decimalSeparator + decRaw,
      symbol: this.symbol(currency),
    };
  }

  formatPartsInBase(
    amount: unknown,
    sourceCurrency?: CurrencyCode,
    dualDisplayOverride?: boolean
  ): {
    primary: ReturnType<CurrencyService['formatParts']>;
    secondary: ReturnType<CurrencyService['formatParts']> | null;
  } {
    const num = this.toNumber(amount);

    const from = sourceCurrency || this.getBaseCurrency();
    const base = this.getBaseCurrency();
    const secondary = this.getSecondaryCurrency();

    const baseAmount = this.convert(num, from, base);
    const primary = this.formatParts(baseAmount, base);

    if (!this.shouldShowDual(dualDisplayOverride)) {
      return { primary, secondary: null };
    }

    const secondaryAmount = this.convert(baseAmount, base, secondary);
    const secondaryParts = this.formatParts(secondaryAmount, secondary);

    return { primary, secondary: secondaryParts };
  }
}
